import './DynamicModelOverview.scss';
import { Fragment, ReactNode } from 'react';
import { ChartContainer } from '../ChartContainer';
import { Size } from '../designConstants';
import { InfoTooltip } from '../InfoTooltip';
import { Spinner } from '../Spinner';

export interface DynamicModelOverviewRow {
  label: string | ReactNode;
  value: string | ReactNode;
  tooltipText?: string;
}

export interface DynamicModelOverviewSection {
  title: string;
  rows: DynamicModelOverviewRow[];
  subsections?: DynamicModelOverviewRow[][];
}

interface DynamicModelOverviewProps {
  title?: string;
  sections: DynamicModelOverviewSection[];
  rightActions?: ReactNode;
  height?: number;
  loading?: boolean;
}

const DataRow = ({ label, value, tooltipText }: DynamicModelOverviewRow) => (
  <div className='data-row'>
    <div className='label'>
      <span>{label}:</span>
      {tooltipText && <InfoTooltip size={Size.SM}>{tooltipText}</InfoTooltip>}
    </div>
    <div className='value'>{value}</div>
  </div>
);

const DynamicModelOverview = ({
  title = 'Model Overview',
  sections,
  rightActions,
  height,
  loading = false
}: DynamicModelOverviewProps) => (
  <div className='dynamic-model-overview' style={height ? { height } : undefined}>
    <ChartContainer
      title={title}
      rightActions={rightActions}
      className='dynamic-model-overview-chart'
    >
      {loading ? (
        <div className='loading-container'>
          <Spinner />
        </div>
      ) : (
        <div className='chart-body'>
          {sections.map(({ title: sectionTitle, rows, subsections = [] }, sectionIndex) => (
            <Fragment key={`section-${sectionIndex}`}>
              <div className='data-section'>
                {sectionTitle && <div className='data-section-title'>{sectionTitle}</div>}
                <div className='data-body'>
                  {rows.map(({ label, value, tooltipText }, rowIndex) => (
                    <DataRow
                      key={`section-${sectionIndex}-row-${rowIndex}`}
                      label={label}
                      value={value}
                      tooltipText={tooltipText}
                    />
                  ))}
                </div>
                {subsections.map((subsection, subsectionIndex) => (
                  <Fragment key={`section-${sectionIndex}-subsection-${subsectionIndex}`}>
                    <hr className='divider subsection-divider' />
                    <div className='data-subsection'>
                      {subsection.map(({ label, value, tooltipText }, rowIndex) => (
                        <DataRow
                          key={`section-${sectionIndex}-subsection-${subsectionIndex}-row-${rowIndex}`}
                          label={label}
                          value={value}
                          tooltipText={tooltipText}
                        />
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
              {sectionIndex < sections.length - 1 && <hr className='divider section-divider' />}
            </Fragment>
          ))}
        </div>
      )}
    </ChartContainer>
  </div>
);

export { DynamicModelOverview };
